import React, { useEffect, useState } from 'react';
import {
    Typography,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Alert,
    CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import apiService from '../services/api';

/**
 * Read-only view of the rules the report checker applies to every generated
 * report (the findings surfaced by LintWarning). The rules come from
 * GET /api/linter-rules, which parses the checker's own prompt, so this page
 * always shows exactly what the checker is told.
 */

// Rule text is markdown written for the model; keep it compact in table cells.
const Markdown = ({ children }) => (
    <Box
        sx={{
            '& p': { m: 0 },
            '& p + p, & p + ul, & ul + p': { mt: 1 },
            '& ul': { m: 0, pl: 2.5 },
            '& li + li': { mt: 0.5 },
            '& code': { fontFamily: 'monospace', fontSize: '0.85em', bgcolor: 'grey.100', px: 0.5, borderRadius: 0.5 },
        }}
    >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>
    </Box>
);

const RulePart = ({ title, children }) => (
    <Box>
        <Typography variant="subtitle2" gutterBottom>{title}</Typography>
        <Markdown>{children}</Markdown>
    </Box>
);

const RuleCard = ({ rule }) => (
    <Accordion
        data-rule={rule.id}
        variant="outlined"
        disableGutters
        TransitionProps={{ unmountOnExit: true }}
        sx={{ mb: 1, borderRadius: 1, '&:before': { display: 'none' } }}
    >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ pr: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography variant="subtitle1" component="h4" sx={{ fontWeight: 600 }}>
                        {rule.name}
                    </Typography>
                    <Chip
                        label={rule.severity}
                        size="small"
                        color={rule.severity === 'FAIL' ? 'error' : 'warning'}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {rule.id}
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {rule.summary}
                </Typography>
            </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'grid', gap: 2, pt: 0 }}>
            <RulePart title="What it checks">{rule.check}</RulePart>
            {rule.how && <RulePart title="How it is checked">{rule.how}</RulePart>}
            {rule.edge_cases && (
                <RulePart title="Usually correct — not flagged">{rule.edge_cases}</RulePart>
            )}
        </AccordionDetails>
    </Accordion>
);

// Rules in prompt order, bucketed by group (groups in order of first appearance).
const groupRules = (rules) => {
    const groups = [];
    rules.forEach((rule) => {
        const name = rule.group || 'Other';
        let group = groups.find((g) => g.name === name);
        if (!group) {
            group = { name, rules: [] };
            groups.push(group);
        }
        group.rules.push(rule);
    });
    return groups;
};

const LinterRules = () => {
    const [catalogue, setCatalogue] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        apiService.getLinterRules()
            .then((response) => {
                if (!cancelled) setCatalogue(response.data);
            })
            .catch((err) => {
                console.error('Error loading report checks:', err);
                if (!cancelled) setError('Could not load the report checks. Please try again.');
            });
        return () => {
            cancelled = true;
        };
    }, []);

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }
    if (!catalogue) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Report Checks
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Every generated report is checked against its own charts and tables before you see it.
                An AI checker ({catalogue.model}) reads each section and applies the rules below.
                A <strong>FAIL</strong> means a statement contradicts the data; a <strong>WARN</strong> means
                it likely needs a human look.
            </Typography>

            {catalogue.active_linter !== 'llm' && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    The AI checker is switched off, so these rules are not currently being applied.
                    Reports are being checked by the older rule-based linter instead.
                </Alert>
            )}

            <Box sx={{ mb: 4 }}>
                {groupRules(catalogue.rules).map((group) => (
                    <Box key={group.name} data-group-section={group.name} sx={{ mb: 3 }}>
                        <Typography variant="h6" component="h3" gutterBottom data-group>
                            {group.name}
                        </Typography>
                        {group.rules.map((rule) => (
                            <RuleCard key={rule.id} rule={rule} />
                        ))}
                    </Box>
                ))}
            </Box>

            <Typography variant="h6" gutterBottom>
                Thresholds
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Fixed business rules the checker applies exactly.
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Threshold</TableCell>
                            <TableCell>Value</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {catalogue.house_rules.map((row) => (
                            <TableRow key={row.rule} sx={{ verticalAlign: 'top' }}>
                                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{row.rule}</TableCell>
                                <TableCell>
                                    <Markdown>{row.value}</Markdown>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="h6" gutterBottom>
                Never flagged
            </Typography>
            <Paper sx={{ p: 2, mb: 4 }}>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {catalogue.never_flagged.map((item) => (
                        <Box component="li" key={item.title} sx={{ mb: 1 }}>
                            <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                                {item.title}.
                            </Typography>{' '}
                            <Box component="span" sx={{ '& p': { display: 'inline' } }}>
                                <Markdown>{item.text}</Markdown>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Paper>
        </Box>
    );
};

export default LinterRules;
